import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Common";
import { fmtMoney, fmtDateTime, fmtDate, fmtTime } from "@/lib/format";
import { ListChecks, MagnifyingGlass, Calculator, CaretUpDown, Copy, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATUSES = ["", "draft", "accepted", "completed", "declined", "archived"];

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [status, setStatus] = useState("");
  const [quotedBy, setQuotedBy] = useState("");
  const [quoters, setQuoters] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [mode, setMode] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState(-1);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/quotes/meta/quoters").then((r) => setQuoters(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const params = { sort, order };
    if (status) params.status = status;
    if (quotedBy) params.quoted_by = quotedBy;
    if (vehicleId) params.vehicle_id = vehicleId;
    if (mode) params.mode = mode;
    if (dateFrom) params.date_from = `${dateFrom}T00:00:00+00:00`;
    if (dateTo) params.date_to = `${dateTo}T23:59:59+00:00`;
    if (q) params.q = q;
    const { data } = await api.get("/quotes", { params });
    setQuotes(data);
  }, [status, quotedBy, vehicleId, mode, dateFrom, dateTo, q, sort, order]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (field) => {
    if (sort === field) setOrder(order === -1 ? 1 : -1);
    else { setSort(field); setOrder(-1); }
  };

  const duplicate = async (e, id) => { e.stopPropagation(); const { data } = await api.post(`/quotes/${id}/duplicate`); toast.success("Duplicated as draft"); navigate(`/quotes/${data.id}`); };

  const Th = ({ field, children, className }) => (
    <th className={`px-4 py-3 text-left cursor-pointer select-none ${className || ""}`} onClick={() => toggleSort(field)} data-testid={`sort-${field}`}>
      <span className="inline-flex items-center gap-1">{children}<CaretUpDown size={12} className={sort === field ? "text-slate-900" : "text-slate-300"} /></span>
    </th>
  );

  return (
    <div>
      <PageHeader title="Quotes & History" subtitle="All quotes with latest revisions."
        actions={<Button data-testid="quotes-new" onClick={() => navigate("/quote")} className="bg-slate-900 hover:bg-slate-800 text-white"><Calculator size={18} weight="bold" className="mr-2" />New Quote</Button>} />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="quotes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search quote #, client, title…"
            className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 outline-none" />
        </div>
        <select data-testid="quotes-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white capitalize">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <select data-testid="quotes-user-filter" value={quotedBy} onChange={(e) => setQuotedBy(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white max-w-[180px]">
          <option value="">All users</option>
          {quoters.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <select data-testid="quotes-vehicle-filter" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white max-w-[180px]">
          <option value="">All vehicles</option>
          {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select data-testid="quotes-type-filter" value={mode} onChange={(e) => setMode(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
          <option value="">All types</option>
          <option value="own">Own fleet</option>
          <option value="external">3rd party</option>
        </select>
        <div className="flex items-center gap-1">
          <input type="date" data-testid="quotes-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="Trip date from" className="px-2 py-2 border border-slate-300 rounded-md text-sm bg-white text-slate-600" />
          <span className="text-slate-400 text-sm">–</span>
          <input type="date" data-testid="quotes-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="Trip date to" className="px-2 py-2 border border-slate-300 rounded-md text-sm bg-white text-slate-600" />
        </div>
        {(status || quotedBy || vehicleId || mode || dateFrom || dateTo || q) && (
          <button data-testid="quotes-clear-filters" onClick={() => { setStatus(""); setQuotedBy(""); setVehicleId(""); setMode(""); setDateFrom(""); setDateTo(""); setQ(""); }}
            className="px-3 py-2 text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><X size={14} />Clear</button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto cd-scroll">
        {quotes.length === 0 ? <EmptyState icon={ListChecks} title="No quotes" hint="Create your first quote to get started." /> : (
          <table className="w-full text-sm min-w-[940px]">
            <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <tr>
                <Th field="quote_number">Quote #</Th>
                <th className="px-4 py-3 text-left">Client</th>
                <th className="px-4 py-3 text-left">Vehicle</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Quoted by</th>
                <th className="px-4 py-3 text-right">Total excl VAT</th>
                <th className="px-4 py-3 text-left">Margin</th>
                <Th field="trip_start">Trip date</Th>
                <th className="px-4 py-3 text-left">Trip time</th>
                <Th field="created_at">Created</Th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {quotes.map((qt) => (
                <tr key={qt.id} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => navigate(`/quotes/${qt.id}`)} data-testid={`quote-row-${qt.id}`}>
                  <td className="px-4 py-3 font-mono font-semibold text-slate-900">{qt.quote_number} <span className="text-xs text-slate-400 font-sans">v{qt.version}</span></td>
                  <td className="px-4 py-3 text-slate-700">{qt.client_snapshot?.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{qt.vehicle_snapshot?.name || "—"}</td>
                  <td className="px-4 py-3" data-testid={`quote-type-${qt.id}`}>
                    {qt.quote_mode === "external"
                      ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">3rd party</span>
                      : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200">Own</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs" data-testid={`quote-user-${qt.id}`}>{qt.created_by_name || "—"}</td>
                  <td className="px-4 py-3 text-right data-num font-medium text-slate-900">{fmtMoney(qt.costing?.selling_ex_vat)}</td>
                  <td className="px-4 py-3">
                    <span className={`data-num text-xs ${qt.costing?.warning === "red" ? "text-red-600" : qt.costing?.warning === "amber" ? "text-amber-600" : "text-slate-600"}`}>{qt.costing?.margin_pct}%</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-xs whitespace-nowrap" data-testid={`quote-tripdate-${qt.id}`}>{fmtDate(qt.trip_start)}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap data-num" data-testid={`quote-triptime-${qt.id}`}>{qt.trip_start ? `${fmtTime(qt.trip_start)}–${fmtTime(qt.trip_end)}` : "—"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDateTime(qt.created_at)}</td>
                  <td className="px-4 py-3"><StatusBadge status={qt.status} /></td>
                  <td className="px-4 py-3 text-right"><button onClick={(e) => duplicate(e, qt.id)} className="p-1.5 text-slate-400 hover:text-slate-900" title="Duplicate"><Copy size={16} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
